import { create } from 'zustand';
import type { FileHandleRef, ViewMode, EditorSource } from '@slate/core';
import { basename, isUnderRoot } from '../fs/containment';
import { SORTABLE_HIT_SECTIONS, type SortableHitSection } from '../palette/types';
import { type ToolsSnapshot } from '../tools/toolsStore';
import { resolveSoftWrap, parseSoftWrapByLanguage } from '../util/softWrap';
import { langKeyForDoc } from '../util/docLanguage';

export type ThemePref = 'light' | 'dark' | 'system';

/**
 * Typography theme = {Sans, Serif} × {Reader, High-contrast}. INDEPENDENT of the
 * light/dark color mode (`themePref`). Controls the prose typeface (sans/serif —
 * code is ALWAYS mono) and the palette character (reader = soft/low-contrast,
 * contrast = crisp). Persisted to `slate:theme`.
 */
export type ThemeName =
  | 'sans-contrast'
  | 'sans-reader'
  | 'serif-contrast'
  | 'serif-reader';

export const THEME_NAMES: readonly ThemeName[] = [
  'sans-contrast',
  'sans-reader',
  'serif-contrast',
  'serif-reader',
];

const DEFAULT_THEME: ThemeName = 'sans-contrast';

function isThemeName(v: unknown): v is ThemeName {
  return (
    v === 'sans-contrast' ||
    v === 'sans-reader' ||
    v === 'serif-contrast' ||
    v === 'serif-reader'
  );
}

/** One open document buffer. Source of truth per doc is the markdown `text`. */
export interface OpenDoc {
  /** `ref.id` for saved files; `untitled:N` for untitled/clipboard buffers. */
  id: string;
  ref: FileHandleRef | null;
  text: string;
  dirty: boolean;
  /** Which editor last wrote the text (dirty-per-view sync, spec §3.2). */
  lastEditor: EditorSource;
  /**
   * What this doc renders as. `'text'` (default) → markdown/code/text editor +
   * views. `'image'` → a read-only image viewer that reads bytes from `ref` on
   * demand (its `text` is unused / empty). Optional for back-compat (absent ⇒
   * `'text'`).
   */
  kind?: 'text' | 'image';
  /**
   * Per-document language override (a `LANGUAGE_CHOICES` key, e.g. `'typescript'`).
   * When set, the document behaves exactly as if its filename had that language's
   * extension: syntax colors, available views, outline, palette commands, the tab
   * icon, and the default save name all follow it. Cleared (returned to
   * extension-derived behavior) by `setDocLanguage(id, null)`. Absent ⇒ no
   * override (the real extension, or the Plain Text default for untitled, drives).
   */
  langOverride?: string;
  /**
   * Transient, per-document soft-wrap override. `undefined` ⇒ no override (wrap
   * follows the global / per-language config); `true` / `false` ⇒ this document
   * wraps / doesn't, regardless of config. Set by the `/wrap` command and the
   * in-editor Alt+Z binding. Intentionally NOT persisted to the session manifest
   * (sessionManifest.ts serializes an explicit field allow-list, so it is
   * dropped automatically).
   */
  wrapOverride?: boolean;
}

export interface DocStore {
  /** Open-file bookmark-bar order. */
  docs: OpenDoc[];
  activeId: string | null;
  /**
   * Open workspace roots (multi-root). "Open Folder" appends a root; each is
   * shown as its own collapsible group in the sidebar. Search and link
   * resolution span all roots. Deduped by `ref.id`.
   */
  workspaceRoots: FileHandleRef[];
  /** Global view mode (shared across docs). */
  viewMode: ViewMode;
  /** Global split toggle: active editor side-by-side with rendered preview. */
  split: boolean;
  /** Persisted color-mode preference (light/dark/system). */
  themePref: ThemePref;
  /** Persisted typography theme ({sans,serif} × {reader,contrast}). */
  theme: ThemeName;
  /** Whether the left sidebar is collapsed to a thin rail. Persisted. */
  /** Expanded width of the left sidebar in px. Persisted. */
  sidebarWidth: number;
  /** Whether the right outline panel is docked as a column (vs hover overlay). Persisted. */
  outlinePinned: boolean;
  /**
   * Whether file-backed dirty docs auto-save (debounced ~5s, 30s max-wait).
   * Untitled buffers are never auto-saved (they'd need a Save As). Persisted.
   */
  autosave: boolean;
  /** Idle debounce for autosave, in ms (clamped to [AUTOSAVE_DEBOUNCE_MIN_MS, AUTOSAVE_DEBOUNCE_MAX_MS]). Persisted. */
  autosaveDebounceMs: number;
  /** Max-wait deadline for autosave, in ms (clamped to [debounce, AUTOSAVE_MAX_WAIT_CEILING_MS]). Persisted. */
  autosaveMaxWaitMs: number;
  /**
   * Reading-mode text zoom (1.0 = 100% — default). Scales the reading body's
   * font-size; spacing / heading sizes follow via `em` units. Clamped to
   * [{@link READING_FONT_SCALE_MIN}, {@link READING_FONT_SCALE_MAX}]. Persisted.
   */
  readingFontScale: number;
  /**
   * Reading-mode content width: `'narrow'` (centered book column),
   * `'wide'` (centered wider article), or `'full'` (fill the pane).
   * Persisted.
   */
  readingWidth: ReadingWidth;
  /** Terminal cursor shape: block, bar, or underline. Default `'bar'`. Persisted. */
  terminalCursorStyle: TerminalCursorStyle;
  /**
   * Whether Slate-spawned shells get OSC 133 shell integration injected (zsh/bash
   * only). Enables prompt click-to-move. Default `true`. Persisted.
   */
  terminalShellIntegration: boolean;
  /** Global soft-wrap default for the plaintext editor. */
  softWrapDefault: boolean;
  /** When true, non-prose (code) languages don't wrap even when the default is on. */
  softWrapExcludeCode: boolean;
  /** Per-language wrap exceptions, keyed by `LANGUAGE_CHOICES` key. */
  softWrapByLanguage: Record<string, boolean>;
  /**
   * Transient: a 1-based source line the active view should scroll to once it's
   * mounted with the relevant doc (set by a content-search match click; consumed
   * + cleared by App). NOT persisted, NOT reset across docs deliberately.
   */
  pendingJumpLine: number | null;
  /**
   * Doc ids with an unresolved external-change conflict (file changed on disk
   * while the buffer was dirty). The App renders a conflict modal for `[0]`.
   */
  conflicts: string[];
  /**
   * Doc ids the user requested to close (X button / Close / Close others / Close
   * all). The App drains this queue: clean docs close immediately; the first
   * dirty one drives the Save/Don't-save/Cancel guard. NOT persisted.
   */
  closeQueue: string[];
  /** Whether the command palette overlay is open. NOT persisted. */
  paletteOpen: boolean;
  /** Optional seed text the palette consumes on next open (e.g., '/'). NOT persisted. */
  paletteSeed: string | null;
  /**
   * Hard cap on rows rendered in mixed-mode (no-prefix) palette results.
   * Clamped to [{@link PALETTE_MIXED_CAP_MIN}, {@link PALETTE_MIXED_CAP_MAX}]. Persisted.
   */
  paletteMixedCap: number;
  /**
   * Debounce window before the async workspace search fires, in ms. Clamped to
   * [{@link PALETTE_WORKSPACE_DEBOUNCE_MIN_MS}, {@link PALETTE_WORKSPACE_DEBOUNCE_MAX_MS}]. Persisted.
   */
  paletteWorkspaceDebounceMs: number;
  /**
   * Minimum query length before workspace search fires. Clamped to
   * [{@link PALETTE_MIN_WORKSPACE_QUERY_MIN}, {@link PALETTE_MIN_WORKSPACE_QUERY_MAX}]. Persisted.
   */
  paletteMinWorkspaceQuery: number;
  /**
   * When true, Cmd/Ctrl+K opens the palette already seeded with `/` — i.e.
   * directly in command mode rather than mixed-search. Persisted.
   */
  paletteCommandModeDefault: boolean;
  /**
   * User-customizable order of the seven mixed-mode result buckets, drag-
   * sortable from Settings → Command Palette. Persisted as JSON. Unknown
   * entries are dropped on load; missing entries are appended in their
   * default position so a partial saved order never loses a bucket.
   */
  paletteMixedOrder: SortableHitSection[];
  setPaletteOpen: (open: boolean) => void;
  togglePalette: () => void;
  /** Open the palette with `seed` pre-typed (or empty when null). */
  openPaletteWithSeed: (seed: string) => void;
  /** Consume the seed once the palette has read it. */
  consumePaletteSeed: () => void;
  /** Set the mixed-mode row cap. Clamped + persisted. */
  setPaletteMixedCap: (n: number) => void;
  /** Set the workspace-search debounce (ms). Clamped + persisted. */
  setPaletteWorkspaceDebounceMs: (ms: number) => void;
  /** Set the minimum workspace-query length. Clamped + persisted. */
  setPaletteMinWorkspaceQuery: (n: number) => void;
  /** Toggle (or explicitly set) the "open Cmd+K in command mode" flag. Persisted. */
  setPaletteCommandModeDefault: (on: boolean) => void;
  /** Replace the mixed-mode priority order. Coerced + persisted. */
  setPaletteMixedOrder: (order: readonly SortableHitSection[]) => void;
  /** Reset the mixed-mode order back to the built-in default sequence. */
  resetPaletteMixedOrder: () => void;

  /** The currently active document, if any. */
  activeDoc: () => OpenDoc | undefined;
  /**
   * Open a document. Dedupes by `ref.id` (re-activates an already-open file);
   * untitled docs (null ref) always open a new buffer. Activates and returns id.
   */
  openDoc: (ref: FileHandleRef | null, text: string) => string;
  /**
   * Open a read-only IMAGE document. Dedupes by `ref.id` (re-activates an
   * already-open image) like `openDoc`; the viewer reads bytes from `ref` on
   * demand so no text is stored. Activates and returns the id.
   */
  openImageDoc: (ref: FileHandleRef) => string;
  /** Open a new untitled buffer with optional seed text. Returns its id. */
  newUntitled: (text?: string) => string;
  /** Close a doc; re-activates a neighbour (or clears active if none left). */
  closeDoc: (id: string) => void;
  /**
   * Queue one or more docs for a *guarded* close (the App drains the queue,
   * prompting per dirty doc). Use this for user-initiated closes; `closeDoc`
   * closes unconditionally. Deduped; unknown/already-queued ids are ignored.
   */
  requestCloseDocs: (ids: string[]) => void;
  /** Remove an id from the close queue (after it's closed or the user cancels). */
  dequeueClose: (id: string) => void;
  /** Clear the entire close queue (e.g. user cancels a multi-doc close). */
  clearCloseQueue: () => void;
  /**
   * Reorder tabs: remove `dragId` and insert it at `targetId`'s position.
   * No-op when the ids are equal or either is unknown. `activeId` is unchanged.
   */
  moveDoc: (dragId: string, targetId: string, side?: 'before' | 'after') => void;
  activateDoc: (id: string) => void;
  /** Mutate the active doc's text (marks it dirty if changed). */
  setText: (text: string, source: EditorSource) => void;
  /** Mark the active doc saved to `ref` (clears dirty, adopts the ref/id). */
  markSaved: (ref: FileHandleRef) => void;
  /**
   * Set (or clear, with `null`) a document's language override by id. The key is
   * a `LANGUAGE_CHOICES` entry (e.g. `'typescript'`). No-op for an unknown id.
   */
  setDocLanguage: (docId: string, key: string | null) => void;
  /**
   * Replace a doc's text by id (used for clean external auto-reload AND the
   * "Load external" conflict action). Clears dirty; resets lastEditor to 'none'.
   */
  applyExternalReload: (id: string, content: string) => void;
  /** Clear the dirty flag for a doc by id (after "Keep mine" writes to disk). */
  markDocClean: (id: string) => void;
  /** Queue a doc id for conflict resolution (no-op if already queued/unknown). */
  enqueueConflict: (id: string) => void;
  /** Remove a doc id from the conflict queue. */
  resolveConflict: (id: string) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleSplit: () => void;
  /** Append a workspace root; no-op (no duplicate/reorder) if `ref.id` is already open. */
  addWorkspaceRoot: (ref: FileHandleRef) => void;
  /** Remove the workspace root with the given id. */
  removeWorkspaceRoot: (id: string) => void;
  setThemePref: (pref: ThemePref) => void;
  /** Set the typography theme ({sans,serif} × {reader,contrast}). Persisted. */
  setTheme: (theme: ThemeName) => void;
  /** Toggle (or explicitly set) the sidebar collapsed/rail state. Persisted. */
  /** Set the sidebar's expanded width (clamped). Persisted. */
  setSidebarWidth: (px: number) => void;
  /** Toggle (or explicitly set) the docked outline panel. Persisted. */
  toggleOutlinePinned: (pinned?: boolean) => void;
  /** Set the autosave flag (persists + sets). */
  setAutosave: (on: boolean) => void;
  /** Toggle the autosave flag (persists + sets). */
  toggleAutosave: () => void;
  /** Set the idle debounce window (ms). Clamped + persisted. */
  setAutosaveDebounceMs: (ms: number) => void;
  /** Set the max-wait deadline (ms). Clamped + persisted. */
  setAutosaveMaxWaitMs: (ms: number) => void;
  /** Set the reading-mode text zoom (1.0 = 100%). Clamped + persisted. */
  setReadingFontScale: (scale: number) => void;
  /** Nudge the reading-mode text zoom by `delta` (e.g. +0.1 / −0.1). Clamped + persisted. */
  nudgeReadingFontScale: (delta: number) => void;
  /** Reset the reading-mode text zoom to 1.0. Persisted. */
  resetReadingFontScale: () => void;
  /** Set the reading-mode content width. Persisted. */
  setReadingWidth: (width: ReadingWidth) => void;
  /** Set the terminal cursor style (persists + sets). */
  setTerminalCursorStyle: (style: TerminalCursorStyle) => void;
  /** Set the terminal shell-integration flag (persists + sets). */
  setTerminalShellIntegration: (on: boolean) => void;
  setSoftWrapDefault: (on: boolean) => void;
  setSoftWrapExcludeCode: (on: boolean) => void;
  /** Upsert one per-language wrap exception. */
  setSoftWrapLanguage: (key: string, on: boolean) => void;
  /** Remove one per-language wrap exception. */
  clearSoftWrapLanguage: (key: string) => void;
  /** Set (`true`/`false`) or clear (`null`) a document's transient wrap override. */
  setDocWrapOverride: (docId: string, value: boolean | null) => void;
  /** Flip a document's resolved wrap by writing the inverse as its transient override. */
  toggleDocWrap: (docId: string) => void;
  /** Request the active view scroll to a 1-based source line once ready (`null` clears). */
  setPendingJumpLine: (line: number | null) => void;
  /**
   * Apply a sidebar-initiated rename to open doc refs, workspace roots, the
   * active selection, and the close / conflict queues — atomically and
   * without dropping unsaved buffer state. Handles both file renames
   * (`fromPath` matches a doc's path exactly) AND folder renames
   * (`fromPath` is a parent of doc paths — every descendant has its
   * `fromPath` prefix swapped for `toPath`). Text, dirty flag, `lastEditor`,
   * and the doc's tab position are preserved. No-op when `fromPath ===
   * toPath` or nothing references either side.
   */
  applyPathRename: (fromPath: string, toPath: string) => void;
}

const THEME_PREF_KEY = 'slate:themePref';
const THEME_KEY = 'slate:theme';
const SIDEBAR_WIDTH_KEY = 'slate:sidebarWidth';
const OUTLINE_PINNED_KEY = 'slate:outlinePinned';
const AUTOSAVE_KEY = 'slate:autosave';
const AUTOSAVE_DEBOUNCE_KEY = 'slate:autosaveDebounceMs';
const AUTOSAVE_MAX_WAIT_KEY = 'slate:autosaveMaxWaitMs';
const READING_FONT_SCALE_KEY = 'slate:readingFontScale';
const READING_WIDTH_KEY = 'slate:readingWidth';
const TERMINAL_CURSOR_STYLE_KEY = 'slate:terminalCursorStyle';
const TERMINAL_SHELL_INTEGRATION_KEY = 'slate:terminalShellIntegration';
const SOFT_WRAP_DEFAULT_KEY = 'slate:softWrapDefault';
const SOFT_WRAP_EXCLUDE_CODE_KEY = 'slate:softWrapExcludeCode';
const SOFT_WRAP_BY_LANGUAGE_KEY = 'slate:softWrapByLanguage';
const PALETTE_MIXED_CAP_KEY = 'slate:paletteMixedCap';
const PALETTE_WS_DEBOUNCE_KEY = 'slate:paletteWorkspaceDebounceMs';
const PALETTE_MIN_WS_QUERY_KEY = 'slate:paletteMinWorkspaceQuery';
const PALETTE_CMD_MODE_DEFAULT_KEY = 'slate:paletteCommandModeDefault';
const PALETTE_MIXED_ORDER_KEY = 'slate:paletteMixedOrder';

/** Idle debounce defaults + sane clamp bounds. The ceiling is generous (60s)
 *  so power-users can choose to defer saves; the floor (250ms) avoids paging
 *  the FS on every keystroke. */
export const AUTOSAVE_DEBOUNCE_DEFAULT_MS = 3_000;
export const AUTOSAVE_DEBOUNCE_MIN_MS = 250;
export const AUTOSAVE_DEBOUNCE_MAX_MS = 60_000;
export const AUTOSAVE_MAX_WAIT_DEFAULT_MS = 15_000;
export const AUTOSAVE_MAX_WAIT_CEILING_MS = 300_000;

/** Reading-mode text zoom. Default 1.0; bounds picked to keep heading scale
 *  legible at the extremes (very small / very large). Step is exposed for the
 *  +/− controls so the keyboard shortcut and the UI nudge by the same amount. */
export const READING_FONT_SCALE_DEFAULT = 1;
export const READING_FONT_SCALE_MIN = 0.7;
export const READING_FONT_SCALE_MAX = 1.6;
export const READING_FONT_SCALE_STEP = 0.1;

/** Command-palette tuning defaults + clamp bounds. Mixed-cap upper bound is
 *  generous (500) so power-users who want everything inline can opt in; the
 *  workspace-debounce floor allows zero so a fast disk can search per-keystroke.
 *  The query-length range tops out at 5 to discourage pathological values that
 *  would effectively disable workspace search. */
export const PALETTE_MIXED_CAP_DEFAULT = 100;
export const PALETTE_MIXED_CAP_MIN = 25;
export const PALETTE_MIXED_CAP_MAX = 500;
export const PALETTE_WORKSPACE_DEBOUNCE_DEFAULT_MS = 200;
export const PALETTE_WORKSPACE_DEBOUNCE_MIN_MS = 0;
export const PALETTE_WORKSPACE_DEBOUNCE_MAX_MS = 1_000;
export const PALETTE_MIN_WORKSPACE_QUERY_DEFAULT = 2;
export const PALETTE_MIN_WORKSPACE_QUERY_MIN = 1;
export const PALETTE_MIN_WORKSPACE_QUERY_MAX = 5;

/** Reading-mode content width values. `narrow` matches the previous fixed
 *  720px column; `wide` opens up a desktop-article column; `full` fills the
 *  pane (still padded by `.mp-preview--reading`'s outer gutter). */
export type ReadingWidth = 'narrow' | 'wide' | 'full';
export const READING_WIDTHS: readonly ReadingWidth[] = ['narrow', 'wide', 'full'];
const DEFAULT_READING_WIDTH: ReadingWidth = 'narrow';

function isReadingWidth(v: unknown): v is ReadingWidth {
  return v === 'narrow' || v === 'wide' || v === 'full';
}

/** Terminal cursor shape. Matches xterm's `cursorStyle` option values exactly,
 *  so no mapping is needed when wiring the live terminal. */
export type TerminalCursorStyle = 'block' | 'bar' | 'underline';
export const TERMINAL_CURSOR_STYLES: readonly TerminalCursorStyle[] = [
  'block',
  'bar',
  'underline',
];
const DEFAULT_TERMINAL_CURSOR_STYLE: TerminalCursorStyle = 'bar';

function isTerminalCursorStyle(v: unknown): v is TerminalCursorStyle {
  return v === 'block' || v === 'bar' || v === 'underline';
}

function clampReadingFontScale(scale: number): number {
  if (!Number.isFinite(scale)) return READING_FONT_SCALE_DEFAULT;
  // Snap to the nearest step so persisted values stay tidy across reloads.
  const snapped = Math.round(scale / READING_FONT_SCALE_STEP) * READING_FONT_SCALE_STEP;
  // Round trip through a fixed-decimal string to avoid 1.0000000002-style noise.
  const tidied = Number(snapped.toFixed(2));
  return Math.max(READING_FONT_SCALE_MIN, Math.min(READING_FONT_SCALE_MAX, tidied));
}

function loadReadingFontScale(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(READING_FONT_SCALE_KEY));
    if (Number.isFinite(v) && v > 0) return clampReadingFontScale(v);
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return READING_FONT_SCALE_DEFAULT;
}

function persistReadingFontScale(scale: number): void {
  try {
    globalThis.localStorage?.setItem(READING_FONT_SCALE_KEY, String(scale));
  } catch {
    /* ignore persistence failures */
  }
}

function loadReadingWidth(): ReadingWidth {
  try {
    const v = globalThis.localStorage?.getItem(READING_WIDTH_KEY);
    if (isReadingWidth(v)) return v;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_READING_WIDTH;
}

function persistReadingWidth(width: ReadingWidth): void {
  try {
    globalThis.localStorage?.setItem(READING_WIDTH_KEY, width);
  } catch {
    /* ignore persistence failures */
  }
}

function loadTerminalCursorStyle(): TerminalCursorStyle {
  try {
    const v = globalThis.localStorage?.getItem(TERMINAL_CURSOR_STYLE_KEY);
    if (isTerminalCursorStyle(v)) return v;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_TERMINAL_CURSOR_STYLE;
}

function persistTerminalCursorStyle(style: TerminalCursorStyle): void {
  try {
    globalThis.localStorage?.setItem(TERMINAL_CURSOR_STYLE_KEY, style);
  } catch {
    /* ignore persistence failures */
  }
}

function loadTerminalShellIntegration(): boolean {
  try {
    const v = globalThis.localStorage?.getItem(TERMINAL_SHELL_INTEGRATION_KEY);
    // Default ON: only an explicit '0' disables it. A missing key (first run)
    // and any other value fall through to true.
    if (v === '0') return false;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return true;
}

function persistTerminalShellIntegration(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(TERMINAL_SHELL_INTEGRATION_KEY, on ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

function loadSoftWrapDefault(): boolean {
  try {
    // Default ON: only an explicit '0' disables it.
    return globalThis.localStorage?.getItem(SOFT_WRAP_DEFAULT_KEY) !== '0';
  } catch {
    return true;
  }
}

function persistSoftWrapDefault(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOFT_WRAP_DEFAULT_KEY, on ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

function loadSoftWrapExcludeCode(): boolean {
  try {
    // Default ON: only an explicit '0' disables it.
    return globalThis.localStorage?.getItem(SOFT_WRAP_EXCLUDE_CODE_KEY) !== '0';
  } catch {
    return true;
  }
}

function persistSoftWrapExcludeCode(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOFT_WRAP_EXCLUDE_CODE_KEY, on ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

function loadSoftWrapByLanguage(): Record<string, boolean> {
  try {
    return parseSoftWrapByLanguage(globalThis.localStorage?.getItem(SOFT_WRAP_BY_LANGUAGE_KEY) ?? null);
  } catch {
    return {};
  }
}

function persistSoftWrapByLanguage(map: Record<string, boolean>): void {
  try {
    globalThis.localStorage?.setItem(SOFT_WRAP_BY_LANGUAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore persistence failures */
  }
}
/** Persisted session snapshot: open workspace roots + saved-file tabs + active id. */
const SESSION_KEY = 'slate:session:v1';

export const SIDEBAR_MIN_WIDTH = 180;
export const SIDEBAR_MAX_WIDTH = 480;
const SIDEBAR_DEFAULT_WIDTH = 248;

/**
 * Spec §4.1: very large docs (> ~500 KB) default to plaintext+preview and
 * WYSIWYG is disabled (Crepe / ProseMirror don't scale gracefully past this
 * size). Measured in UTF-16 code units (`string.length`), which is what JS
 * strings count natively.
 */
export const LARGE_DOC_THRESHOLD = 500_000;

export function isLargeDoc(text: string): boolean {
  return text.length > LARGE_DOC_THRESHOLD;
}

function loadThemePref(): ThemePref {
  try {
    const v = globalThis.localStorage?.getItem(THEME_PREF_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage unavailable (SSR / private mode) — fall back to default */
  }
  return 'system';
}

function persistThemePref(pref: ThemePref): void {
  try {
    globalThis.localStorage?.setItem(THEME_PREF_KEY, pref);
  } catch {
    /* ignore persistence failures */
  }
}

function loadTheme(): ThemeName {
  try {
    const v = globalThis.localStorage?.getItem(THEME_KEY);
    if (isThemeName(v)) return v;
  } catch {
    /* localStorage unavailable (SSR / private mode) — fall back to default */
  }
  return DEFAULT_THEME;
}

function persistTheme(theme: ThemeName): void {
  try {
    globalThis.localStorage?.setItem(THEME_KEY, theme);
  } catch {
    /* ignore persistence failures */
  }
}

function clampSidebarWidth(px: number): number {
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, Math.round(px)));
}


function loadSidebarWidth(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(SIDEBAR_WIDTH_KEY));
    if (Number.isFinite(v) && v > 0) return clampSidebarWidth(v);
  } catch {
    /* fall through to default */
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

function persistSidebarWidth(px: number): void {
  try {
    globalThis.localStorage?.setItem(SIDEBAR_WIDTH_KEY, String(px));
  } catch {
    /* ignore persistence failures */
  }
}

function loadOutlinePinned(): boolean {
  try {
    return globalThis.localStorage?.getItem(OUTLINE_PINNED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistOutlinePinned(pinned: boolean): void {
  try {
    globalThis.localStorage?.setItem(OUTLINE_PINNED_KEY, pinned ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

function loadAutosave(): boolean {
  try {
    return globalThis.localStorage?.getItem(AUTOSAVE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistAutosave(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(AUTOSAVE_KEY, on ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

function clampDebounceMs(ms: number): number {
  if (!Number.isFinite(ms)) return AUTOSAVE_DEBOUNCE_DEFAULT_MS;
  return Math.max(AUTOSAVE_DEBOUNCE_MIN_MS, Math.min(AUTOSAVE_DEBOUNCE_MAX_MS, Math.round(ms)));
}

function clampMaxWaitMs(ms: number, debounceMs: number): number {
  if (!Number.isFinite(ms)) return AUTOSAVE_MAX_WAIT_DEFAULT_MS;
  return Math.max(debounceMs, Math.min(AUTOSAVE_MAX_WAIT_CEILING_MS, Math.round(ms)));
}

function loadAutosaveDebounceMs(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(AUTOSAVE_DEBOUNCE_KEY));
    if (Number.isFinite(v) && v > 0) return clampDebounceMs(v);
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return AUTOSAVE_DEBOUNCE_DEFAULT_MS;
}

function persistAutosaveDebounceMs(ms: number): void {
  try {
    globalThis.localStorage?.setItem(AUTOSAVE_DEBOUNCE_KEY, String(ms));
  } catch {
    /* ignore persistence failures */
  }
}

function loadAutosaveMaxWaitMs(debounceMs: number): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(AUTOSAVE_MAX_WAIT_KEY));
    if (Number.isFinite(v) && v > 0) return clampMaxWaitMs(v, debounceMs);
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return Math.max(debounceMs, AUTOSAVE_MAX_WAIT_DEFAULT_MS);
}

function persistAutosaveMaxWaitMs(ms: number): void {
  try {
    globalThis.localStorage?.setItem(AUTOSAVE_MAX_WAIT_KEY, String(ms));
  } catch {
    /* ignore persistence failures */
  }
}

function clampInt(n: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function loadPaletteMixedCap(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(PALETTE_MIXED_CAP_KEY));
    if (Number.isFinite(v) && v > 0) {
      return clampInt(v, PALETTE_MIXED_CAP_MIN, PALETTE_MIXED_CAP_MAX, PALETTE_MIXED_CAP_DEFAULT);
    }
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return PALETTE_MIXED_CAP_DEFAULT;
}

function persistPaletteMixedCap(n: number): void {
  try {
    globalThis.localStorage?.setItem(PALETTE_MIXED_CAP_KEY, String(n));
  } catch {
    /* ignore persistence failures */
  }
}

function loadPaletteWorkspaceDebounceMs(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(PALETTE_WS_DEBOUNCE_KEY));
    // Allow 0 explicitly — that's a valid "search every keystroke" choice.
    if (Number.isFinite(v) && v >= 0) {
      return clampInt(
        v,
        PALETTE_WORKSPACE_DEBOUNCE_MIN_MS,
        PALETTE_WORKSPACE_DEBOUNCE_MAX_MS,
        PALETTE_WORKSPACE_DEBOUNCE_DEFAULT_MS,
      );
    }
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return PALETTE_WORKSPACE_DEBOUNCE_DEFAULT_MS;
}

function persistPaletteWorkspaceDebounceMs(ms: number): void {
  try {
    globalThis.localStorage?.setItem(PALETTE_WS_DEBOUNCE_KEY, String(ms));
  } catch {
    /* ignore persistence failures */
  }
}

function loadPaletteMinWorkspaceQuery(): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(PALETTE_MIN_WS_QUERY_KEY));
    if (Number.isFinite(v) && v > 0) {
      return clampInt(
        v,
        PALETTE_MIN_WORKSPACE_QUERY_MIN,
        PALETTE_MIN_WORKSPACE_QUERY_MAX,
        PALETTE_MIN_WORKSPACE_QUERY_DEFAULT,
      );
    }
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return PALETTE_MIN_WORKSPACE_QUERY_DEFAULT;
}

function persistPaletteMinWorkspaceQuery(n: number): void {
  try {
    globalThis.localStorage?.setItem(PALETTE_MIN_WS_QUERY_KEY, String(n));
  } catch {
    /* ignore persistence failures */
  }
}

function loadPaletteCommandModeDefault(): boolean {
  try {
    return globalThis.localStorage?.getItem(PALETTE_CMD_MODE_DEFAULT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Coerce an arbitrary string array into a valid `paletteMixedOrder`. Unknown
 * entries are dropped, duplicates collapsed, and any missing default sections
 * appended in their default position so a partial saved order never loses a
 * bucket across upgrades.
 */
function normalizePaletteMixedOrder(input: readonly unknown[]): SortableHitSection[] {
  const known = new Set<string>(SORTABLE_HIT_SECTIONS);
  const seen = new Set<string>();
  const out: SortableHitSection[] = [];
  for (const v of input) {
    if (typeof v !== 'string') continue;
    if (!known.has(v) || seen.has(v)) continue;
    seen.add(v);
    out.push(v as SortableHitSection);
  }
  for (const def of SORTABLE_HIT_SECTIONS) {
    if (!seen.has(def)) out.push(def);
  }
  return out;
}

function loadPaletteMixedOrder(): SortableHitSection[] {
  try {
    const raw = globalThis.localStorage?.getItem(PALETTE_MIXED_ORDER_KEY);
    if (!raw) return [...SORTABLE_HIT_SECTIONS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...SORTABLE_HIT_SECTIONS];
    return normalizePaletteMixedOrder(parsed);
  } catch {
    return [...SORTABLE_HIT_SECTIONS];
  }
}

function persistPaletteMixedOrder(order: SortableHitSection[]): void {
  try {
    globalThis.localStorage?.setItem(PALETTE_MIXED_ORDER_KEY, JSON.stringify(order));
  } catch {
    /* ignore persistence failures */
  }
}

function arraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function persistPaletteCommandModeDefault(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(PALETTE_CMD_MODE_DEFAULT_KEY, on ? '1' : '0');
  } catch {
    /* ignore persistence failures */
  }
}

let untitledSeq = 0;
function nextUntitledId(): string {
  untitledSeq += 1;
  return `untitled:${untitledSeq}`;
}

/** Pick the id to activate after closing `closingId` from `docs`. */
function neighbourId(docs: OpenDoc[], closingId: string): string | null {
  const idx = docs.findIndex((d) => d.id === closingId);
  if (idx < 0) return docs[0]?.id ?? null;
  const next = docs[idx + 1] ?? docs[idx - 1];
  return next?.id ?? null;
}

export const useDocStore = create<DocStore>((set, get) => ({
  docs: [],
  activeId: null,
  workspaceRoots: [],
  viewMode: 'plaintext',
  split: false,
  themePref: loadThemePref(),
  theme: loadTheme(),
  sidebarWidth: loadSidebarWidth(),
  outlinePinned: loadOutlinePinned(),
  autosave: loadAutosave(),
  autosaveDebounceMs: loadAutosaveDebounceMs(),
  autosaveMaxWaitMs: loadAutosaveMaxWaitMs(loadAutosaveDebounceMs()),
  readingFontScale: loadReadingFontScale(),
  readingWidth: loadReadingWidth(),
  terminalCursorStyle: loadTerminalCursorStyle(),
  terminalShellIntegration: loadTerminalShellIntegration(),
  softWrapDefault: loadSoftWrapDefault(),
  softWrapExcludeCode: loadSoftWrapExcludeCode(),
  softWrapByLanguage: loadSoftWrapByLanguage(),
  pendingJumpLine: null,
  conflicts: [],
  closeQueue: [],
  paletteOpen: false,
  paletteSeed: null,
  paletteMixedCap: loadPaletteMixedCap(),
  paletteWorkspaceDebounceMs: loadPaletteWorkspaceDebounceMs(),
  paletteMinWorkspaceQuery: loadPaletteMinWorkspaceQuery(),
  paletteCommandModeDefault: loadPaletteCommandModeDefault(),
  paletteMixedOrder: loadPaletteMixedOrder(),

  activeDoc: () => {
    const { docs, activeId } = get();
    return docs.find((d) => d.id === activeId);
  },

  openDoc: (ref, text) => {
    if (ref) {
      const existing = get().docs.find((d) => d.id === ref.id);
      if (existing) {
        set({ activeId: existing.id });
        return existing.id;
      }
    }
    const id = ref ? ref.id : nextUntitledId();
    const doc: OpenDoc = { id, ref, text, dirty: false, lastEditor: 'none', kind: 'text' };
    set((s) => {
      // Very large docs default to plaintext (WYSIWYG would freeze on mount).
      const viewMode = isLargeDoc(text) && s.viewMode === 'wysiwyg' ? 'plaintext' : s.viewMode;
      return { docs: [...s.docs, doc], activeId: id, viewMode };
    });
    return id;
  },

  openImageDoc: (ref) => {
    const existing = get().docs.find((d) => d.id === ref.id);
    if (existing) {
      set({ activeId: existing.id });
      return existing.id;
    }
    const doc: OpenDoc = {
      id: ref.id,
      ref,
      text: '',
      dirty: false,
      lastEditor: 'none',
      kind: 'image',
    };
    set((s) => ({ docs: [...s.docs, doc], activeId: ref.id }));
    return ref.id;
  },

  newUntitled: (text = '') => {
    const id = nextUntitledId();
    const doc: OpenDoc = { id, ref: null, text, dirty: false, lastEditor: 'none' };
    set((s) => ({ docs: [...s.docs, doc], activeId: id }));
    return id;
  },

  closeDoc: (id) =>
    set((s) => {
      const docs = s.docs.filter((d) => d.id !== id);
      const activeId = s.activeId === id ? neighbourId(s.docs, id) : s.activeId;
      // Closing also drops the id from the close queue so a drained id can't
      // re-prompt (e.g. "Don't save" path).
      const closeQueue = s.closeQueue.includes(id)
        ? s.closeQueue.filter((q) => q !== id)
        : s.closeQueue;
      return { docs, activeId, closeQueue };
    }),

  requestCloseDocs: (ids) =>
    set((s) => {
      const known = new Set(s.docs.map((d) => d.id));
      const seen = new Set(s.closeQueue);
      const add: string[] = [];
      for (const id of ids) {
        if (!known.has(id) || seen.has(id)) continue;
        seen.add(id); // dedupe within `ids` too
        add.push(id);
      }
      if (add.length === 0) return s;
      return { closeQueue: [...s.closeQueue, ...add] };
    }),

  dequeueClose: (id) =>
    set((s) => {
      if (!s.closeQueue.includes(id)) return s;
      return { closeQueue: s.closeQueue.filter((q) => q !== id) };
    }),

  clearCloseQueue: () => set((s) => (s.closeQueue.length ? { closeQueue: [] } : s)),

  moveDoc: (dragId, targetId, side = 'before') =>
    set((s) => {
      if (dragId === targetId) return s;
      const from = s.docs.findIndex((d) => d.id === dragId);
      const to = s.docs.findIndex((d) => d.id === targetId);
      if (from < 0 || to < 0) return s; // unknown id → no-op
      const docs = s.docs.slice();
      const [moved] = docs.splice(from, 1);
      // After removal the target's index may have shifted; recompute. `side`
      // picks the insertion edge of the target so the dragged tab lands
      // exactly where the insertion-preview line was drawn.
      let insertAt = docs.findIndex((d) => d.id === targetId);
      if (insertAt < 0) insertAt = docs.length;
      if (side === 'after') insertAt += 1;
      docs.splice(insertAt, 0, moved);
      return { docs };
    }),

  activateDoc: (id) => set({ activeId: id }),

  setText: (text, source) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === s.activeId);
      if (idx < 0) return s;
      const cur = s.docs[idx];
      if (cur.text === text) return s;
      const docs = s.docs.slice();
      docs[idx] = { ...cur, text, dirty: true, lastEditor: source };
      return { docs };
    }),

  markSaved: (ref) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === s.activeId);
      if (idx < 0) return s;
      const docs = s.docs.slice();
      // Saving adopts the ref (untitled -> file): the doc's id becomes ref.id.
      docs[idx] = { ...docs[idx], id: ref.id, ref, dirty: false };
      return { docs, activeId: ref.id };
    }),

  setDocLanguage: (docId, key) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === docId);
      if (idx < 0) return s; // unknown id — no-op
      const cur = s.docs[idx];
      const next = key ?? undefined;
      if (cur.langOverride === next) return s;
      const docs = s.docs.slice();
      docs[idx] = { ...cur, langOverride: next };
      return { docs };
    }),

  applyExternalReload: (id, content) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === id);
      if (idx < 0) return s;
      const docs = s.docs.slice();
      docs[idx] = { ...docs[idx], text: content, dirty: false, lastEditor: 'none' };
      // External reload (e.g. file changed on disk) can push a doc past the
      // large-doc threshold; bounce WYSIWYG to plaintext if this is the
      // active doc and it's now large.
      const viewMode =
        s.activeId === id && isLargeDoc(content) && s.viewMode === 'wysiwyg'
          ? 'plaintext'
          : s.viewMode;
      return { docs, viewMode };
    }),

  markDocClean: (id) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === id);
      if (idx < 0 || !s.docs[idx].dirty) return s;
      const docs = s.docs.slice();
      docs[idx] = { ...docs[idx], dirty: false };
      return { docs };
    }),

  enqueueConflict: (id) =>
    set((s) => {
      if (s.conflicts.includes(id) || !s.docs.some((d) => d.id === id)) return s;
      return { conflicts: [...s.conflicts, id] };
    }),

  resolveConflict: (id) =>
    set((s) => {
      if (!s.conflicts.includes(id)) return s;
      return { conflicts: s.conflicts.filter((c) => c !== id) };
    }),

  setViewMode: (viewMode) => set({ viewMode }),
  toggleSplit: () => set((s) => ({ split: !s.split })),
  setPaletteOpen: (open) => set({ paletteOpen: open }),
  togglePalette: () => set((s) => ({ paletteOpen: !s.paletteOpen })),
  openPaletteWithSeed: (seed) => set({ paletteOpen: true, paletteSeed: seed }),
  consumePaletteSeed: () => set({ paletteSeed: null }),

  setPaletteMixedCap: (n) =>
    set((s) => {
      const next = clampInt(n, PALETTE_MIXED_CAP_MIN, PALETTE_MIXED_CAP_MAX, PALETTE_MIXED_CAP_DEFAULT);
      if (next === s.paletteMixedCap) return s;
      persistPaletteMixedCap(next);
      return { paletteMixedCap: next };
    }),

  setPaletteWorkspaceDebounceMs: (ms) =>
    set((s) => {
      const next = clampInt(
        ms,
        PALETTE_WORKSPACE_DEBOUNCE_MIN_MS,
        PALETTE_WORKSPACE_DEBOUNCE_MAX_MS,
        PALETTE_WORKSPACE_DEBOUNCE_DEFAULT_MS,
      );
      if (next === s.paletteWorkspaceDebounceMs) return s;
      persistPaletteWorkspaceDebounceMs(next);
      return { paletteWorkspaceDebounceMs: next };
    }),

  setPaletteMinWorkspaceQuery: (n) =>
    set((s) => {
      const next = clampInt(
        n,
        PALETTE_MIN_WORKSPACE_QUERY_MIN,
        PALETTE_MIN_WORKSPACE_QUERY_MAX,
        PALETTE_MIN_WORKSPACE_QUERY_DEFAULT,
      );
      if (next === s.paletteMinWorkspaceQuery) return s;
      persistPaletteMinWorkspaceQuery(next);
      return { paletteMinWorkspaceQuery: next };
    }),

  setPaletteCommandModeDefault: (on) =>
    set((s) => {
      if (on === s.paletteCommandModeDefault) return s;
      persistPaletteCommandModeDefault(on);
      return { paletteCommandModeDefault: on };
    }),

  setPaletteMixedOrder: (order) =>
    set((s) => {
      const next = normalizePaletteMixedOrder(order);
      if (arraysEqual(next, s.paletteMixedOrder)) return s;
      persistPaletteMixedOrder(next);
      return { paletteMixedOrder: next };
    }),
  resetPaletteMixedOrder: () =>
    set((s) => {
      const next = [...SORTABLE_HIT_SECTIONS];
      if (arraysEqual(next, s.paletteMixedOrder)) return s;
      persistPaletteMixedOrder(next);
      return { paletteMixedOrder: next };
    }),

  addWorkspaceRoot: (ref) =>
    set((s) => {
      if (s.workspaceRoots.some((r) => r.id === ref.id)) return s; // dedupe by id
      return { workspaceRoots: [...s.workspaceRoots, ref] };
    }),

  removeWorkspaceRoot: (id) =>
    set((s) => {
      if (!s.workspaceRoots.some((r) => r.id === id)) return s;
      return { workspaceRoots: s.workspaceRoots.filter((r) => r.id !== id) };
    }),

  setThemePref: (themePref) => {
    persistThemePref(themePref);
    set({ themePref });
  },

  setTheme: (theme) => {
    persistTheme(theme);
    set({ theme });
  },

  setSidebarWidth: (px) => {
    const width = clampSidebarWidth(px);
    persistSidebarWidth(width);
    set({ sidebarWidth: width });
  },

  toggleOutlinePinned: (pinned) =>
    set((s) => {
      const next = pinned ?? !s.outlinePinned;
      persistOutlinePinned(next);
      return { outlinePinned: next };
    }),

  setAutosave: (on) => {
    persistAutosave(on);
    set({ autosave: on });
  },

  toggleAutosave: () =>
    set((s) => {
      const next = !s.autosave;
      persistAutosave(next);
      return { autosave: next };
    }),

  setAutosaveDebounceMs: (ms) =>
    set((s) => {
      const debounce = clampDebounceMs(ms);
      // Bump max-wait up to the new debounce floor if needed; never reduce it
      // silently (a user who raised it stays raised).
      const maxWait = Math.max(debounce, s.autosaveMaxWaitMs);
      persistAutosaveDebounceMs(debounce);
      if (maxWait !== s.autosaveMaxWaitMs) persistAutosaveMaxWaitMs(maxWait);
      return { autosaveDebounceMs: debounce, autosaveMaxWaitMs: maxWait };
    }),

  setAutosaveMaxWaitMs: (ms) =>
    set((s) => {
      const maxWait = clampMaxWaitMs(ms, s.autosaveDebounceMs);
      persistAutosaveMaxWaitMs(maxWait);
      return { autosaveMaxWaitMs: maxWait };
    }),

  setReadingFontScale: (scale) =>
    set((s) => {
      const next = clampReadingFontScale(scale);
      if (next === s.readingFontScale) return s;
      persistReadingFontScale(next);
      return { readingFontScale: next };
    }),

  nudgeReadingFontScale: (delta) =>
    set((s) => {
      const next = clampReadingFontScale(s.readingFontScale + delta);
      if (next === s.readingFontScale) return s;
      persistReadingFontScale(next);
      return { readingFontScale: next };
    }),

  resetReadingFontScale: () =>
    set((s) => {
      if (s.readingFontScale === READING_FONT_SCALE_DEFAULT) return s;
      persistReadingFontScale(READING_FONT_SCALE_DEFAULT);
      return { readingFontScale: READING_FONT_SCALE_DEFAULT };
    }),

  setReadingWidth: (width) =>
    set((s) => {
      if (width === s.readingWidth) return s;
      persistReadingWidth(width);
      return { readingWidth: width };
    }),

  setTerminalCursorStyle: (style) =>
    set((s) => {
      if (style === s.terminalCursorStyle) return s;
      persistTerminalCursorStyle(style);
      return { terminalCursorStyle: style };
    }),

  setTerminalShellIntegration: (on) =>
    set((s) => {
      if (on === s.terminalShellIntegration) return s;
      persistTerminalShellIntegration(on);
      return { terminalShellIntegration: on };
    }),

  setSoftWrapDefault: (on) =>
    set((s) => {
      if (on === s.softWrapDefault) return s;
      persistSoftWrapDefault(on);
      return { softWrapDefault: on };
    }),

  setSoftWrapExcludeCode: (on) =>
    set((s) => {
      if (on === s.softWrapExcludeCode) return s;
      persistSoftWrapExcludeCode(on);
      return { softWrapExcludeCode: on };
    }),

  setSoftWrapLanguage: (key, on) =>
    set((s) => {
      if (s.softWrapByLanguage[key] === on) return s;
      const next = { ...s.softWrapByLanguage, [key]: on };
      persistSoftWrapByLanguage(next);
      return { softWrapByLanguage: next };
    }),

  clearSoftWrapLanguage: (key) =>
    set((s) => {
      if (!(key in s.softWrapByLanguage)) return s;
      const next = { ...s.softWrapByLanguage };
      delete next[key];
      persistSoftWrapByLanguage(next);
      return { softWrapByLanguage: next };
    }),

  setDocWrapOverride: (docId, value) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === docId);
      if (idx < 0) return s;
      const cur = s.docs[idx];
      const next = value ?? undefined;
      if (cur.wrapOverride === next) return s;
      const docs = s.docs.slice();
      docs[idx] = { ...cur, wrapOverride: next };
      return { docs };
    }),

  toggleDocWrap: (docId) =>
    set((s) => {
      const idx = s.docs.findIndex((d) => d.id === docId);
      if (idx < 0) return s;
      const cur = s.docs[idx];
      const resolved = resolveSoftWrap(langKeyForDoc(cur), cur.wrapOverride, {
        softWrapDefault: s.softWrapDefault,
        softWrapExcludeCode: s.softWrapExcludeCode,
        softWrapByLanguage: s.softWrapByLanguage,
      });
      const docs = s.docs.slice();
      docs[idx] = { ...cur, wrapOverride: !resolved };
      return { docs };
    }),

  setPendingJumpLine: (pendingJumpLine) => set({ pendingJumpLine }),

  applyPathRename: (fromPath, toPath) =>
    set((s) => {
      if (!fromPath || !toPath || fromPath === toPath) return s;
      // `idMap` collects every (oldId → newId) rewrite so we can also
      // remap `activeId`, `closeQueue`, and `conflicts` in lock-step with
      // the docs themselves. Anything not present in the map stays as-is.
      const idMap = new Map<string, string>();
      const rewritePath = (p: string): string | null => {
        if (p === fromPath) return toPath;
        if (isUnderRoot(p, fromPath)) return toPath + p.slice(fromPath.length);
        return null;
      };
      let dirty = false;
      const docs = s.docs.map((d) => {
        const path = d.ref?.path;
        if (!path) return d;
        const next = rewritePath(path);
        if (next === null) return d;
        dirty = true;
        const name = basename(next);
        const newRef: FileHandleRef = { id: next, path: next, name };
        idMap.set(d.id, next);
        // Preserve text, dirty flag, lastEditor, and kind — we ONLY change
        // identity. The buffer's unsaved edits keep applying to the file
        // under its new path, and the tab stays at the same position.
        return { ...d, id: next, ref: newRef };
      });
      const workspaceRoots = s.workspaceRoots.map((r) => {
        const path = r.path;
        if (!path) return r;
        const next = rewritePath(path);
        if (next === null) return r;
        dirty = true;
        return { id: next, path: next, name: basename(next) };
      });
      if (!dirty) return s;
      const remap = (id: string): string => idMap.get(id) ?? id;
      return {
        docs,
        workspaceRoots,
        activeId: s.activeId ? remap(s.activeId) : s.activeId,
        conflicts: s.conflicts.map(remap),
        closeQueue: s.closeQueue.map(remap),
      };
    }),
}));

// ───────────────── Session persistence ─────────────────
//
// On every change to `workspaceRoots` / `docs` / `activeId`, write a
// snapshot to localStorage so the next launch can restore the same
// opened folders + saved-file tabs + active tab. Untitled buffers (no
// `ref.path`) are excluded — they have no on-disk identity to read back.
// Reading the snapshot is done by the App via {@link loadSessionSnapshot}
// at mount; that's where async readFile + openDoc happen.

/** Shape of the persisted session blob. */
export interface SessionSnapshot {
  workspaceRoots: { id: string; path?: string; name: string }[];
  openDocs: { id: string; path: string; name: string; langOverride?: string }[];
  activeId: string | null;
  /**
   * Tool panel snapshot (open tools, active tool, panel geometry). Optional so
   * sessions written before tool persistence still load cleanly.
   */
  toolsSnapshot?: ToolsSnapshot;
}

/** Read the saved session snapshot, or null when missing / malformed. */
export function loadSessionSnapshot(): SessionSnapshot | null {
  try {
    const raw = globalThis.localStorage?.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as SessionSnapshot).workspaceRoots) ||
      !Array.isArray((parsed as SessionSnapshot).openDocs)
    ) {
      return null;
    }
    return parsed as SessionSnapshot;
  } catch {
    return null;
  }
}

// Session persistence moved to useSessionPersistence (writes the native session
// store via PlatformBridge.session). loadSessionSnapshot is retained only for a
// one-time migration of the legacy localStorage snapshot.

/**
 * Narrow selector hooks — these return scalar/derived values so subscribers
 * re-render only when their specific slice changes (Zustand's default
 * `Object.is` equality). The store still holds `docs` as an array, but
 * consumers that only need the active text don't re-render when an inactive
 * doc's state changes, or when the docs array is re-allocated for an
 * unrelated mutation. Prefer these over `(s) => s.docs.find(...)` in hot paths
 * (editor body, preview, etc.).
 */
export const useActiveDoc = (): OpenDoc | undefined =>
  useDocStore((s) => s.docs.find((d) => d.id === s.activeId));

export const useActiveText = (): string =>
  useDocStore((s) => s.docs.find((d) => d.id === s.activeId)?.text ?? '');

export const useActiveDirty = (): boolean =>
  useDocStore((s) => s.docs.find((d) => d.id === s.activeId)?.dirty ?? false);

export const useActiveKind = (): 'text' | 'image' =>
  useDocStore((s) => s.docs.find((d) => d.id === s.activeId)?.kind ?? 'text');

export const useActiveLastEditor = (): EditorSource =>
  useDocStore((s) => s.docs.find((d) => d.id === s.activeId)?.lastEditor ?? 'none');
